/**
 * Shipped ADP mapping presets (Time Evaluation Phase 4).
 *
 * ⚠️ READ THIS BEFORE CHANGING A COLUMN NAME. An ADP Workforce Now paydata grid
 * is configured PER COMPANY: the column set, the column ORDER and the number of
 * Hours/Earnings slots all come from that client's WFN setup. `WFN_PAYDATA_PRESET`
 * below is the standard grid and a STARTING POINT, not a spec. The real workflow
 * is: copy the preset -> have the client paste their own header row out of WFN ->
 * `parseAdpHeaderRow()` re-binds the columns to their exact header text. That
 * paste path is the difference between "works" and "ADP rejects the file".
 *
 * ⚠️ Treat these constants as READ-ONLY. Copy before editing (the profile editor
 * persists a copy into `payroll_export_profiles`); mutating a shared preset in
 * place changes it for every tenant in the session.
 */
import type { AdpColumnMapping, AdpMappingProfile } from './adpMappingProfile'
import { ADP_FIRST_SLOT_ORDINAL } from './adpMappingProfile'
import { cloneEpipLayout, DEFAULT_EPIP_LAYOUT } from './adpEpipLayout'

// ─── ADP Workforce Now — Paydata CSV ───────────────────────────────────────────

/**
 * The WFN paydata grid, in ADP's own column order.
 *
 * Semantics that drive the serializer (`payroll/serializeAdpPaydata.ts`):
 *
 * • **Row granularity.** ADP paydata is PERIOD level by default: ONE row per
 *   (Co Code x File # x Batch). It is not a daily timecard feed. `rowGranularity:
 *   'day'` is opt-in, for the clients whose WFN grid is configured with a work
 *   date column.
 *
 * • **`Co Code` IS the payroll tenant.** A File # is only unique WITHIN a Co
 *   Code, which is exactly why `partitionPayrollBatch.ts` exists: one file per
 *   tenant, never a merged file. A worker whose tenant does not resolve to a Co
 *   Code is a BLOCKER, never a blank cell.
 *
 * • **`O/T Hours` takes HOURS ONLY, never a multiplier and never dollars.** ADP
 *   applies the associate's own configured OT factor on its side. Sending 1.5x
 *   hours pays them 2.25x.
 *
 * • **`Hours 3/4 Code` + `Amount` is where the premiums land.** 3rd shift, night
 *   differential, holiday-worked and double-time premium HOURS go here — a code
 *   in the Code cell, the hours in the Amount cell. This is the critical mapping
 *   surface of the whole feature; everything else is plumbing.
 *
 * • **`Earnings 3/4 Code` + `Amount` is DOLLAR denominated.** Flat premiums
 *   (lead, hazard, jury, bereavement, NY spread-of-hours) and bonuses. Putting
 *   hours in an Earnings slot pays the worker $8.00 instead of 8 hours.
 *
 * • **`Reg Earnings` is a dollar override, generally mutually exclusive with
 *   `Reg Hours`.** Populating both is how you accidentally pay someone twice, so
 *   the preset emits the column (the header has to line up) and leaves it blank.
 *
 * • **`Memo Code` / `Memo Amount`** are informational and non-paying. Same
 *   treatment: emitted, blank.
 *
 * Defaults: CRLF (ADP's importer is Windows-oriented), 2 hours slots + 2 earnings
 * slots, `spill_extra_row` overflow (WFN does accept multiple rows per associate
 * in one batch), header row on.
 */
const WFN_COLUMNS: AdpColumnMapping[] = [
  /**
   * The ADP company code — 3 characters, and the tenant boundary for every other
   * id in the row. Comes from `resolveCoCode(profile, line.payrollTenant)`.
   */
  { column: 'Co Code', source: { kind: 'field', field: 'coCode' }, format: 'text' },
  /**
   * Groups the rows of one submission inside WFN so payroll can find, review and
   * (if needed) delete them as a unit. Rendered from `batchIdTemplate`.
   */
  { column: 'Batch ID', source: { kind: 'field', field: 'batchId' }, format: 'text' },
  /**
   * The associate's ADP File # — unique only WITHIN the Co Code. Sourced from
   * `external_id_map` (source_system='adp', external_id=File #,
   * source_tenant=Co Code); a worker with no row here is a blocker.
   */
  { column: 'File #', source: { kind: 'field', field: 'fileNumber' }, format: 'text' },
  /**
   * Straight-time worked hours. Paid at the associate's ADP-side base rate; the
   * app never sends the rate itself unless `Temp Rate` is used.
   */
  { column: 'Reg Hours', source: { kind: 'earningsHours', earningsCodes: ['REG'] }, format: 'hours2' },
  /**
   * Overtime HOURS ONLY. ADP multiplies by the associate's configured OT factor.
   * Double time does NOT belong here — it goes into an Hours slot with a DT code.
   */
  { column: 'O/T Hours', source: { kind: 'earningsHours', earningsCodes: ['OT'] }, format: 'hours2' },
  /** Premium slot 3 — the ADP earnings code for the premium hours in slot 3. */
  { column: 'Hours 3 Code', source: { kind: 'hoursSlotCode', slot: 3 }, format: 'text' },
  /** Premium slot 3 — HOURS (not dollars) paid against the slot-3 code. */
  { column: 'Hours 3 Amount', source: { kind: 'hoursSlotAmount', slot: 3 }, format: 'hours2' },
  /** Premium slot 4 — second premium hours code for the same associate/period. */
  { column: 'Hours 4 Code', source: { kind: 'hoursSlotCode', slot: 4 }, format: 'text' },
  /** Premium slot 4 — HOURS paid against the slot-4 code. */
  { column: 'Hours 4 Amount', source: { kind: 'hoursSlotAmount', slot: 4 }, format: 'hours2' },
  /** Dollar slot 3 — the ADP earnings code for the flat amount in slot 3. */
  { column: 'Earnings 3 Code', source: { kind: 'earningsSlotCode', slot: 3 }, format: 'text' },
  /** Dollar slot 3 — a flat DOLLAR amount (lead, hazard, jury, spread-of-hours). */
  { column: 'Earnings 3 Amount', source: { kind: 'earningsSlotAmount', slot: 3 }, format: 'money2' },
  /** Dollar slot 4 — second flat-dollar code for the same associate/period. */
  { column: 'Earnings 4 Code', source: { kind: 'earningsSlotCode', slot: 4 }, format: 'text' },
  /** Dollar slot 4 — the flat DOLLAR amount for the slot-4 code. */
  { column: 'Earnings 4 Amount', source: { kind: 'earningsSlotAmount', slot: 4 }, format: 'money2' },
  /**
   * Temporary department override for this row's hours — redirects the labor
   * distribution without changing the associate's home department in ADP.
   */
  { column: 'Temp Dept', source: { kind: 'field', field: 'tempDept' }, format: 'text' },
  /**
   * Temporary hourly RATE override for this row. Only populate it deliberately:
   * it silently overrides the associate's ADP rate for every hour on the row.
   */
  { column: 'Temp Rate', source: { kind: 'field', field: 'tempRate' }, format: 'money2' },
  /** Informational, non-paying memo code. Emitted for header alignment, blank. */
  { column: 'Memo Code', source: { kind: 'blank' }, format: 'text' },
  /** Informational, non-paying memo amount. Emitted for header alignment, blank. */
  { column: 'Memo Amount', source: { kind: 'blank' }, format: 'money2' },
  /**
   * DOLLAR override for regular pay, generally mutually exclusive with
   * `Reg Hours`. Left blank in the preset on purpose — populating both pays twice.
   */
  { column: 'Reg Earnings', source: { kind: 'blank' }, format: 'money2' },
]

export const WFN_PAYDATA_PRESET: AdpMappingProfile = {
  id: 'adp-wfn-paydata',
  name: 'ADP Workforce Now - Paydata',
  vendorFormat: 'wfn_paydata_csv',
  /**
   * EMPTY ON PURPOSE. There is no such thing as a default Co Code — it is issued
   * per client per ADP company, and inventing one would route a real payroll
   * into the wrong company. `validateAdpProfile` reports this as the preset's
   * one outstanding problem until the client fills it in. Key '' is the
   * untagged / single-tenant entry; '*' is a catch-all.
   */
  companyCodeByTenant: {},
  /** 'TS' + period-end MMDD, e.g. 'TS0131'. ADP batch ids are short. */
  batchIdTemplate: 'TS{PERIOD_END:MMDD}',
  /**
   * Internal earnings code -> ADP earnings code.
   *
   * These four are the canonical buckets `buildPayrollBatch.ts` emits
   * (`PayrollEarningCode`), mapped IDENTITY as a placeholder. Every premium code
   * a client actually uses comes from their own `earnings_codes` catalog and has
   * to be added here (or carried on `earnings_codes.hcm_code`, which
   * `mapEarningsCodeWithFallback` reads as the fallback).
   *
   * ⚠️ Identity is deliberately obvious-wrong rather than plausible-wrong: an
   * unknown code makes ADP reject or drop the line, whereas a guessed short code
   * could collide with a real code in the client's catalog and pay the wrong
   * thing. CONFIRM EVERY ENTRY against the client's ADP earnings code list
   * before a live run.
   */
  earningsCodeMap: {
    REG: 'REG',
    OT: 'OT',
    DT: 'DT',
    LV: 'LV',
  },
  regularCodes: ['REG'],
  overtimeCodes: ['OT'],
  columns: WFN_COLUMNS,
  hoursSlots: 2,
  earningsSlots: 2,
  slotOverflow: 'spill_extra_row',
  rowGranularity: 'period',
  includeHeaderRow: true,
  lineEnding: 'crlf',
  emptyValue: '',
}

// ─── Generic CSV fallback ──────────────────────────────────────────────────────

/** Build the Code/Amount column pair for one dynamic slot. */
function slotPair(
  family: 'Hours' | 'Earnings',
  ordinal: number,
): AdpColumnMapping[] {
  if (family === 'Hours') {
    return [
      { column: `Hours ${ordinal} Code`, source: { kind: 'hoursSlotCode', slot: ordinal }, format: 'text' },
      { column: `Hours ${ordinal} Amount`, source: { kind: 'hoursSlotAmount', slot: ordinal }, format: 'hours2' },
    ]
  }
  return [
    { column: `Earnings ${ordinal} Code`, source: { kind: 'earningsSlotCode', slot: ordinal }, format: 'text' },
    { column: `Earnings ${ordinal} Amount`, source: { kind: 'earningsSlotAmount', slot: ordinal }, format: 'money2' },
  ]
}

const GENERIC_SLOT_COUNT = 4
const GENERIC_SLOT_ORDINALS = Array.from(
  { length: GENERIC_SLOT_COUNT },
  (_v, i) => ADP_FIRST_SLOT_ORDINAL + i,
)

/**
 * The non-WFN fallback: a plain, self-describing CSV for clients on another
 * payroll bureau (or on a WFN contract without a paydata grid), who load the
 * file by hand or hand it to their bureau.
 *
 * Differences from the WFN preset that matter: no `Co Code` column (so no Co
 * Code map is required), LF line endings, and 4 hours + 4 earnings slots because
 * nothing here is competing for a fixed 2-slot grid.
 */
export const GENERIC_CSV_PRESET: AdpMappingProfile = {
  id: 'generic-csv',
  name: 'Generic payroll CSV',
  vendorFormat: 'generic_csv',
  companyCodeByTenant: {},
  batchIdTemplate: '{PERIOD_END:MMDD}',
  earningsCodeMap: {
    REG: 'REG',
    OT: 'OT',
    DT: 'DT',
    LV: 'LV',
  },
  regularCodes: ['REG'],
  overtimeCodes: ['OT'],
  columns: [
    /** The worker's payroll id (ADP File # where one exists). */
    { column: 'Employee ID', source: { kind: 'field', field: 'fileNumber' }, format: 'text' },
    /** Display only — makes the file reviewable by a human before it is loaded. */
    { column: 'Employee Name', source: { kind: 'field', field: 'employeeName' }, format: 'text' },
    /** The raw payroll tenant, untranslated (no Co Code map needed). */
    { column: 'Payroll Tenant', source: { kind: 'field', field: 'payrollTenant' }, format: 'text' },
    /** Groups one submission's rows. */
    { column: 'Batch ID', source: { kind: 'field', field: 'batchId' }, format: 'text' },
    /** Pay period end date. */
    { column: 'Period End', source: { kind: 'field', field: 'periodEnd' }, format: 'date_mmddyyyy' },
    /** Populated only when `rowGranularity` is 'day'; blank at period granularity. */
    { column: 'Work Date', source: { kind: 'field', field: 'workDate' }, format: 'date_mmddyyyy' },
    /** Straight-time worked hours. */
    { column: 'Regular Hours', source: { kind: 'earningsHours', earningsCodes: ['REG'] }, format: 'hours2' },
    /** Overtime HOURS only — the receiving system applies the factor. */
    { column: 'Overtime Hours', source: { kind: 'earningsHours', earningsCodes: ['OT'] }, format: 'hours2' },
    /** Premium HOURS slots (code + hours). */
    ...GENERIC_SLOT_ORDINALS.flatMap((o) => slotPair('Hours', o)),
    /** Flat DOLLAR slots (code + amount). */
    ...GENERIC_SLOT_ORDINALS.flatMap((o) => slotPair('Earnings', o)),
  ],
  hoursSlots: GENERIC_SLOT_COUNT,
  earningsSlots: GENERIC_SLOT_COUNT,
  slotOverflow: 'spill_extra_row',
  rowGranularity: 'period',
  includeHeaderRow: true,
  lineEnding: 'lf',
  emptyValue: '',
}

// ─── ADP EPIP — record-typed HDR / DET / TRL payroll input ─────────────────────

/**
 * The EPIP payroll input file (Codan's ADP load), as defined 2026-09-21: see
 * `DEFAULT_EPIP_LAYOUT` in `adpEpipLayout.ts`. SAP identifiers throughout
 * (SAP company code, S/4 earnings code, SAP personnel number as the employee
 * number), hours only, MM/DD/YYYY, a heading line above each block, retros in
 * a separate RETRO batch. It validates clean as shipped: nothing needs mapping.
 *
 * The ADP-translation options are still there for a client that wants them
 * (`epip.companyCodeSource` / `earningsCodeSource` = 'adp_map'); then
 * `companyCodeByTenant` and `earningsCodeMap` below are read. REG / OT / DT map
 * to themselves; premium codes are deliberately NOT guessed.
 *
 * `columns` is empty and the slot counts are 0: an EPIP profile has no grid.
 */
export const ADP_EPIP_PRESET: AdpMappingProfile = {
  id: 'adp-epip',
  name: 'ADP EPIP (HDR / DET / TRL)',
  vendorFormat: 'adp_epip',
  companyCodeByTenant: {},
  batchIdTemplate: 'TS{PERIOD_END:MMDD}',
  earningsCodeMap: {
    REG: 'REG',
    OT: 'OT',
    DT: 'DT',
  },
  regularCodes: ['REG'],
  overtimeCodes: ['OT'],
  columns: [],
  hoursSlots: 0,
  earningsSlots: 0,
  slotOverflow: 'spill_extra_row',
  rowGranularity: 'period',
  includeHeaderRow: false,
  lineEnding: 'crlf',
  emptyValue: '',
  epip: cloneEpipLayout(DEFAULT_EPIP_LAYOUT),
}

/** Every shipped preset, for a "start from…" picker in the profile editor. */
export const ADP_PROFILE_PRESETS: AdpMappingProfile[] = [
  WFN_PAYDATA_PRESET,
  GENERIC_CSV_PRESET,
  ADP_EPIP_PRESET,
]

/** Look a shipped preset up by id. */
export function getAdpProfilePreset(id: string): AdpMappingProfile | undefined {
  return ADP_PROFILE_PRESETS.find((p) => p.id === id)
}

/**
 * Deep copy of a preset, so the caller can edit it without touching the shared
 * constant. Use this in the profile editor before persisting a client profile.
 */
export function cloneAdpProfile(
  base: AdpMappingProfile,
  overrides: Partial<AdpMappingProfile> = {},
): AdpMappingProfile {
  return {
    ...base,
    companyCodeByTenant: { ...base.companyCodeByTenant },
    earningsCodeMap: { ...base.earningsCodeMap },
    regularCodes: [...base.regularCodes],
    overtimeCodes: [...base.overtimeCodes],
    columns: base.columns.map((c) => ({ ...c, source: { ...c.source } })),
    ...(base.epip ? { epip: cloneEpipLayout(base.epip) } : {}),
    ...overrides,
  }
}
